import { STAGE_TEMPLATE } from "./problem-record-data";

export type DemoStage = {
  key: string;
  label: string;
  state: "SIMULATED_COMPLETE" | "SIMULATED_PENDING";
  evidenceId: string;
  evidence: string;
  owner: string;
  reviewer: string;
};

export type DemoInvestigation = {
  id: string;
  title: string;
  classification: "SYNTHETIC";
  authority: "DEMONSTRATION_ONLY";
  productionUseProhibited: true;
  readOnly: true;
  notice: string;
  historicalClaimIds: readonly string[];
  simulatedClaimIds: readonly string[];
  simulatedPostClaimIds: readonly string[];
  stages: readonly DemoStage[];
  outcome: "SIMULATED_CLOSED" | "SIMULATED_AWAITING_VALIDATION";
  metrics: {
    populationId: string;
    unitsPerWindow: number;
    baselineWindow: string;
    postWindow: string;
    baselineClaims: number;
    postClaims: number | null;
    targetClaims: number;
    approvedSavings: null;
  };
};

const evidence: Record<string, string> = {
  signal:
    "Fictional connector-latch complaints: 30 simulated claims in 1,000 units; no Ford/Nissan linkage.",
  definition:
    "Fictional Atlas program, connector lot DEMO-L01; intermittent latch retention after vibration.",
  containment:
    "Simulated quarantine of DEMO-L01 and 100% retention screening; illustrative log only.",
  "root-cause":
    "Fictional teardown/DOE supports molding-tool wear; escape: missing retention check; systemic: review gap.",
  corrective:
    "Simulated tool refurbishment and retention-force check address occurrence and escape mechanisms.",
  eco: "Fictional ECO-DEMO-001 revises latch tolerance and inspection instructions; simulated release only.",
  pfmea:
    "Fictional PFMEA-DEMO-001 and CP-DEMO-001 link the retention risk to the revised detection control.",
  validation:
    "Fictional 30-unit vibration/retention protocol: all 30 meet the defined 45 N minimum; simulated pass.",
  effectiveness:
    "Comparable fictional 1,000-unit windows: baseline 30 claims, post-change 5, target at most 10.",
  "read-across":
    "Fictional Atlas-B applicability reviewed; simulated owner confirms the equivalent latch control.",
  prevention:
    "Fictional sponsor reviews validation, effectiveness and read-across receipts; simulated closure only.",
};

function makeScenario(pending: boolean): DemoInvestigation {
  const id = pending ? "DEMO-JOURNEY-PENDING" : "DEMO-JOURNEY-CLOSED";
  return {
    id,
    title: "Synthetic example — connector latch investigation",
    classification: "SYNTHETIC",
    authority: "DEMONSTRATION_ONLY",
    productionUseProhibited: true,
    readOnly: true,
    notice:
      "Fictional demonstration. Separate from real Ford/Nissan history. No customer finding, actual approval or production command.",
    historicalClaimIds: [],
    simulatedClaimIds: Array.from({ length: 30 }, (_, i) => `${id}-CLAIM-${i + 1}`),
    simulatedPostClaimIds: pending
      ? []
      : Array.from({ length: 5 }, (_, i) => `${id}-POST-CLAIM-${i + 1}`),
    outcome: pending ? "SIMULATED_AWAITING_VALIDATION" : "SIMULATED_CLOSED",
    stages: STAGE_TEMPLATE.map((stage) => {
      const unfinished = pending && stage.order >= 8;
      return {
        key: stage.key,
        label: stage.label,
        state: unfinished ? "SIMULATED_PENDING" : "SIMULATED_COMPLETE",
        evidenceId: `${id}-EVIDENCE-${stage.order}`,
        evidence: unfinished
          ? `Fictional ${stage.label} work pending; no passed result, approval or closure is asserted.`
          : evidence[stage.key],
        owner: "Demo engineering role",
        reviewer: stage.key === "prevention" ? "Demo sponsor role" : "Demo quality reviewer role",
      };
    }),
    metrics: {
      populationId: "DEMO-ATLAS-CONNECTOR-1000",
      unitsPerWindow: 1000,
      baselineWindow: "2026-07-01/2026-07-31",
      postWindow: "2026-08-01/2026-08-31",
      baselineClaims: 30,
      postClaims: pending ? null : 5,
      targetClaims: 10,
      approvedSavings: null,
    },
  };
}

/** Explicit opt-in only. Never use this as a fallback for a failed live request. */
export function getDemoInvestigation(id: string): DemoInvestigation | null {
  if (id === "DEMO-JOURNEY-CLOSED") return makeScenario(false);
  if (id === "DEMO-JOURNEY-PENDING") return makeScenario(true);
  return null;
}
